<div *ngIf="!isLoading">
  <client-termination-reasons-modal
    *ngIf="modalVisible"
    [displayName]="patientName"
  ></client-termination-reasons-modal>
  <!-- Pinned above the breadcrumb: the flag belongs to the client, so it is page-level context rather
       than something a provider has to be on the Client journey tab to see. -->
  <member-chart-risk-flag-banner
    [personaClientId]="patient?.personaClientId"
    [patientId]="patient?.id"
  ></member-chart-risk-flag-banner>

  <ng-container *ngIf="showClientHeader">
      <div
        *ngIf="breadcrumb"
        class="header-breadcrumb-row"
        aria-label="return to client list"
        data-test="clients-breadcrumb"
        [attr.data-dd-action-name]="breadcrumb + '/#' + patient?.id"
      >
        <span
          (click)="goBackToList()"
          class="breadcrumb-link"
          data-test="breadcrumb-link"
        >
          {{ breadcrumb }}
        </span>
        <span>/</span>
        <span>{{ patient?.displayName }}</span>
      </div>

      <div class="header-name-actions-row">
        <h1
          data-test="client-name"
          [attr.data-dd-action-name]="'client #' + patient?.id"
        >
          {{ patientName }}
        </h1>
        <div class="header-right show-desktop">
          @if (displayReferButton$ | async) {
            <iris-tooltip
              [tooltipText]="referToLabel"
              [position]="IrisTooltipPosition.BOTTOM"
            >
              <iris-icon-button
                [icon]="referToIcon"
                [type]="IrisIconButtonTypes.ACTION"
                (click)="handleReferToAction()"
                data-test="referral-button"
                [ariaLabel]="referToLabel"
                [iconSize]="IconSizes.MD"
              >
              </iris-icon-button>
            </iris-tooltip>
          }
          <iris-tooltip
            tooltipText="Message client"
            [position]="IrisTooltipPosition.BOTTOM"
          >
            <iris-icon-button
              gtagCtaEvent
              *ngIf="enableChatIcon"
              label="NCC clicked from client profile page"
              (click)="openChat()"
              ariaLabel="Non Clinical Chat"
              data-test="non-clinical-chat"
              icon="chat_bubble_outline"
              [type]="IrisIconButtonTypes.ACTION"
              [iconSize]="IconSizes.MD"
            >
            </iris-icon-button>
          </iris-tooltip>
          <iris-button
            [buttonType]="IrisButtonTypes.PRIMARY"
            gtagCtaEvent
            label="Schedule client clicked from client profile page"
            routerLink="/scheduling/events/new"
            [queryParams]="{ patientId: clientId | async }"
            [disabled]="!allowScheduling"
            data-test="schedule-session"
            [iconPosition]="IrisButtonIconPositions.LEFT"
          >
            <iris-icon
              name="calendar_add"
              colorToken="$color-neutral-white"
            ></iris-icon>
            Schedule
          </iris-button>
          @if (patientActive) {
            <iris-tooltip
              tooltipText="Mark as inactive"
              [position]="IrisTooltipPosition.BOTTOM"
            >
              <iris-icon-button
                icon="person_off"
                [type]="IrisIconButtonTypes.DANGER"
                (click)="markClientInactive(patient)"
                class="destructive"
                data-test="mark-inactive"
                ariaLabel="Mark client as inactive"
                [iconSize]="IconSizes.MD"
              >
              </iris-icon-button>
            </iris-tooltip>
          } @if (!patientActive) {
            <iris-tooltip
              tooltipText="Mark as active"
              [position]="IrisTooltipPosition.BOTTOM"
            >
              <iris-icon-button
                icon="person_check"
                [type]="IrisIconButtonTypes.ACTION"
                (click)="markClientActive(patient)"
                data-test="mark-active"
                ariaLabel="Mark client as active"
                [iconSize]="IconSizes.MD"
              >
              </iris-icon-button>
            </iris-tooltip>
          }
        </div>
        <div class="header-right show-mobile">
          <iris-tooltip
            tooltipText="Schedule session"
            [position]="IrisTooltipPosition.BOTTOM"
          >
            <iris-icon-button
              gtagCtaEvent
              icon="calendar_add"
              [iconSize]="IconSizes.MD"
              [type]="IrisIconButtonTypes.ACTION"
              label="Schedule client clicked from client profile page"
              routerLink="/scheduling/events/new"
              [queryParams]="{ patientId: clientId | async }"
              [disabled]="!allowScheduling"
              data-test="schedule-session-menu-item"
            >
            </iris-icon-button>
          </iris-tooltip>
          <iris-menu
            #actionListMenu
            [menuItemList]="menuItems()"
            (menuOpened)="setMenuItems()"
            dataTest="client-actions-menu"
            [closeAllOtherMenus]="true"
            kebabIconName="more_horiz"
            [kebabIconType]="IrisIconButtonTypes.NAVIGATION"
            [kebabIconSize]="IconSizes.MD"
          ></iris-menu>
        </div>
      </div>

      <div class="header-client-info-row">
      <div class="client">
        <div class="client-info-wrapper">
          <p>
            Legal Name:
            <span class="span-value">{{ patientLegalName }}</span>
          </p>
          <p>•</p>
          <p data-test="client-dob">
            <iris-icon
              *ngIf="patientUnderAge"
              name="warning"
              [color]="IconColors.DANGER"
              [usage]="IconUsageTypes.DECORATIVE"
              [size]="IconSizes.SM"
            ></iris-icon>
            Age: <span class="span-value">{{ patientAge }}</span>
          </p>
          <p>•</p>
          <p *ngIf="patientLocation">
            Location: <span class="span-value">{{ patientLocation }}</span>
          </p>
        </div>
        <p
          *ngIf="patientUnderAge"
          class="iris-body-responsive client-underage-warning"
        >
          <iris-chip size="large" *ngIf="patientUnderAge" class="client-status minor" data-test="status-minor">
            18
          </iris-chip>
          {{ underageWarningText }}
        </p>
        <div class="client-info-wrapper">
          <div class="client-status-wrapper" data-dd-privacy="allow">
            <iris-chip
              class="client-status not-invited"
              data-test="status-not-invited"
              *ngIf="status == 'not invited'"
              >Not Invited</iris-chip
            >
            <iris-chip
              class="client-status inactive"
              data-test="status-inactive"
              *ngIf="status == 'inactive'"
              >Inactive</iris-chip
            >
            <iris-chip
              class="client-status invited"
              data-test="status-invited"
              *ngIf="status == 'invited'"
              >Invited</iris-chip
            >
          </div>
          <iris-chip
            class="client-status incomplete-account"
            *ngIf="accountIncomplete"
            data-dd-privacy="allow"
          >
            Incomplete Account
          </iris-chip>
        </div>
        <div
          *ngIf="sessionInfo$ | async as sessionInfo"
          class="client-info-wrapper"
          data-dd-privacy="allow"
        >
          <p>
            Number of sessions: <b>{{ sessionInfo.totalCount }}</b>
          </p>
          <p>•</p>
          <p>
            Last:
            <span class="span-value">{{
              (sessionInfo.prev?.startTime
                | localizedDate : { month: "short", day: "numeric" }) ??
                "None"
            }}</span>
          </p>
          <p>•</p>
          <p>
            Upcoming:
            <a
              *ngIf="sessionInfo.next?.startTime; else noneText"
              [routerLink]="['/scheduling/events', sessionInfo.next.id]"
              class="iris-link-quiet-responsive"
            >
              {{
                sessionInfo.next.startTime
                  | localizedDate
                    : {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit"
                      }
              }}
            </a>
            <ng-template #noneText><b>None</b></ng-template>
          </p>
        </div>
      </div>
    </div>
      <div class="tabs" id="client-page-tabs">
        <iris-tab
          [routerLinkActiveOptions]="{ exact: false }"
          [tabItems]="tabItems"
        ></iris-tab>
      </div>
  </ng-container>

  <div [ngClass]="{ 'client-tab-content-wrapper': showClientHeader }">
    <router-outlet (activate)="onOutletAttached($event)"> </router-outlet>
  </div>
  <sondermind-confirmation-modal></sondermind-confirmation-modal>
</div>
<div *ngIf="isLoading" class="loading-container">
  <iris-spinner size="lg"></iris-spinner>
</div>
